import { cva, type VariantProps } from 'class-variance-authority'
import { forwardRef, type HTMLAttributes } from 'react'
import { useComposedRefs } from '@/shared/lib/compose-refs'
import { cn } from '@/shared/lib/utils'
import { type ScrollFadeOrientation, useScrollFade } from './use-scroll-fade'

/**
 * Degradado de un borde: pegado al lado, difumina de `background` a transparente y
 * SÓLO aparece cuando ese lado tiene contenido oculto (`visible`). La orientación
 * decide el eje (ancho vs alto) y la dirección del gradiente; el tamaño, cuánto cubre.
 */
const edgeVariants = cva('pointer-events-none absolute transition-opacity duration-200', {
  variants: {
    orientation: {
      horizontal: 'inset-y-0',
      vertical: 'inset-x-0'
    },
    edge: { start: '', end: '' },
    size: { sm: '', md: '', lg: '' }
  },
  compoundVariants: [
    {
      orientation: 'horizontal',
      edge: 'start',
      class: 'left-0 bg-gradient-to-r from-background to-transparent'
    },
    {
      orientation: 'horizontal',
      edge: 'end',
      class: 'right-0 bg-gradient-to-l from-background to-transparent'
    },
    {
      orientation: 'vertical',
      edge: 'start',
      class: 'top-0 bg-gradient-to-b from-background to-transparent'
    },
    {
      orientation: 'vertical',
      edge: 'end',
      class: 'bottom-0 bg-gradient-to-t from-background to-transparent'
    },
    { orientation: 'horizontal', size: 'sm', class: 'w-6' },
    { orientation: 'horizontal', size: 'md', class: 'w-10' },
    { orientation: 'horizontal', size: 'lg', class: 'w-16' },
    { orientation: 'vertical', size: 'sm', class: 'h-6' },
    { orientation: 'vertical', size: 'md', class: 'h-10' },
    { orientation: 'vertical', size: 'lg', class: 'h-16' }
  ],
  defaultVariants: { orientation: 'horizontal', size: 'md' }
})

export interface ScrollFadeProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof edgeVariants> {
  /** Eje del scroll. Default `horizontal`. */
  orientation?: ScrollFadeOrientation
  /** Clases del nodo que scrollea (el hijo con overflow), p.ej. `flex gap-2`. */
  scrollClassName?: string
}

/**
 * Contenedor con "scroll fade" nativo: envuelve contenido que puede desbordar y pinta
 * un degradado en el borde con contenido oculto, que se desvanece solo al llegar al
 * inicio/fin. El `ref` reenvía al NODO que scrollea (para `scrollTo`, medir, etc.).
 *
 * @example
 * <ScrollFade orientation="horizontal" scrollClassName="flex gap-2">
 *   {tabs}
 * </ScrollFade>
 */
export const ScrollFade = forwardRef<HTMLDivElement, ScrollFadeProps>(
  (
    { orientation = 'horizontal', size, className, scrollClassName, children, ...props },
    forwardedRef
  ) => {
    const { ref, edges } = useScrollFade<HTMLDivElement>(orientation)
    const composedRef = useComposedRefs(ref, forwardedRef)

    return (
      <div className={cn('relative', className)} {...props}>
        <div
          ref={composedRef}
          className={cn(
            'no-scrollbar',
            orientation === 'horizontal' ? 'overflow-x-auto' : 'overflow-y-auto',
            scrollClassName
          )}
        >
          {children}
        </div>
        <div
          aria-hidden
          className={cn(
            edgeVariants({ orientation, edge: 'start', size }),
            edges.start ? 'opacity-100' : 'opacity-0'
          )}
        />
        <div
          aria-hidden
          className={cn(
            edgeVariants({ orientation, edge: 'end', size }),
            edges.end ? 'opacity-100' : 'opacity-0'
          )}
        />
      </div>
    )
  }
)
ScrollFade.displayName = 'ScrollFade'
